import { useEffect } from "react";
import {
  useSharedValue,
  withTiming,
  useAnimatedStyle,
} from "react-native-reanimated";

export const useProgressStyle = ({
  playing,
  currentTime,
  previewDurationInSeconds,
}: {
  playing: boolean;
  currentTime: number;
  previewDurationInSeconds: number;
}) => {
  const progressWidth = useSharedValue(0);

  useEffect(() => {
    if (previewDurationInSeconds === 0) return;

    const currentProgressPercentage = Math.min(
      (currentTime / previewDurationInSeconds) * 100,
      100,
    );

    if (playing) {
      // Calculate remaining time and percentage
      const remainingTime = previewDurationInSeconds - currentTime;
      const remainingPercentage = 100 - currentProgressPercentage;

      if (remainingTime > 0 && remainingPercentage > 0) {
        // Animate smoothly to 100% over the remaining duration
        progressWidth.set(
          withTiming(100, {
            duration: remainingTime * 1000,
          }),
        );
      }
    } else {
      // When paused, set to current position immediately (stops animation)
      progressWidth.set(currentProgressPercentage);
    }
  }, [playing]);

  useEffect(() => {
    if (previewDurationInSeconds === 0) return;

    if (!playing) {
      const currentProgressPercentage = Math.min(
        (currentTime / previewDurationInSeconds) * 100,
        100,
      );

      if (currentTime === 0) {
        // Reset to beginning with animation
        progressWidth.set(withTiming(0, { duration: 300 }));
      } else {
        // Set to current position without animation when paused
        progressWidth.set(currentProgressPercentage);
      }
    }
  }, [currentTime, playing, previewDurationInSeconds, progressWidth]);

  const animatedProgressStyle = useAnimatedStyle(() => ({
    width: `${progressWidth.value}%`,
  }));

  return animatedProgressStyle;
};
